import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminStaffSchedulesApi, adminStaffAbsencesApi } from '@api/client';
import type { SchedulesWeek, StaffAbsence, StaffAbsenceReason, CenterDaySchedule } from '@api/types';
import { useCenters } from '@hooks/useAdminResources';
import { useActiveCenter, ALL_CENTERS } from '@hooks/useActiveCenter';
import * as Icons from '@components/Icons';
import { PageHead } from '@components/admin/PageHead';
import { AbsencesSection } from './AbsencesSection';
import { AssignShiftModal } from './HorariosAssignModal';
import {
  DOWS,
  TIME_RANGES,
  CENTRE_PALETTE,
  DEFAULT_PALETTE,
  centreCode,
  isoDate,
  getMondayOf,
  addDays,
  rangeLabel,
  rangeHours,
  downloadCsv,
  type CentreMeta,
  type StaffRow,
  type DayCell,
} from './horarios-shared';

export function HorariosPage() {
  const [anchor, setAnchor] = useState<Date>(() => getMondayOf(new Date()));
  const { activeCenterId, setActiveCenterId, supportsAll, isSuper } = useActiveCenter();
  const [editing, setEditing] = useState<{ staffId: string; dayOfWeek: number } | null>(null);
  const [copying, setCopying] = useState(false);
  const qc = useQueryClient();

  const weekStart = isoDate(anchor);

  const { data, isLoading, isFetching, error } = useQuery<SchedulesWeek>({
    queryKey: ['staff-schedules', weekStart],
    queryFn: () => adminStaffSchedulesApi.listWeek(weekStart),
    staleTime: 30_000,
  });

  // Absences overlapping the current week (used to overlay "Vacaciones"/"Baja"
  // badges over the weekly grid cells)
  const weekEndIso = isoDate(addDays(anchor, 6));
  const { data: weekAbsences } = useQuery<StaffAbsence[]>({
    queryKey: ['staff-absences-week', weekStart, weekEndIso],
    queryFn: () => adminStaffAbsencesApi.list({ from: weekStart, to: weekEndIso }),
    staleTime: 30_000,
  });

  // Centres (superadmin-only screen) → businessHours per centre for the modal.
  const { data: centersData = [] } = useCenters(true);
  const centresBh = useMemo(() => {
    const m = new Map<string, readonly (CenterDaySchedule | null)[] | null>();
    for (const c of centersData) m.set(c.id, c.businessHours ?? null);
    return m;
  }, [centersData]);

  // Save a shift: one centre per day. When the chosen centre differs from an
  // existing shift that day, clear the old one first (cross-centre replace),
  // then upsert. D1 has no transactions; this two-step is fine for an admin tool.
  const saveShift = useMutation({
    mutationFn: async (v: {
      staffId: string;
      dayOfWeek: number;
      centerId: string;
      timeRange: string;
      oldCenterId: string | null;
    }) => {
      if (v.oldCenterId) {
        await adminStaffSchedulesApi.clear(v.staffId, v.oldCenterId, weekStart, v.dayOfWeek);
      }
      await adminStaffSchedulesApi.upsert({
        staffId: v.staffId,
        centerId: v.centerId,
        weekStart,
        dayOfWeek: v.dayOfWeek,
        timeRange: v.timeRange,
      });
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['staff-schedules', weekStart] }); setEditing(null); },
  });

  const removeShift = useMutation({
    mutationFn: (v: { staffId: string; centerId: string; dayOfWeek: number }) =>
      adminStaffSchedulesApi.clear(v.staffId, v.centerId, weekStart, v.dayOfWeek),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['staff-schedules', weekStart] }); setEditing(null); },
  });

  const copyPrevWeek = useMutation({
    mutationFn: () => {
      const prev = isoDate(addDays(anchor, -7));
      return adminStaffSchedulesApi.copy(prev, weekStart);
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['staff-schedules', weekStart] }); setCopying(false); },
    onError: () => { setCopying(false); },
  });

  // One shift per (staff, day). If legacy data has two centres the same day,
  // the first wins (the new UI prevents creating more than one going forward).
  const cellByDay = useMemo(() => {
    const m = new Map<string, DayCell>();
    for (const c of data?.cells ?? []) {
      const k = `${c.staffId}#${String(c.dayOfWeek)}`;
      if (!m.has(k)) m.set(k, { centerId: c.centerId, timeRange: c.timeRange });
    }
    return m;
  }, [data]);

  // For each (staffId, dayOfWeek) of this week, is the staff on absence?
  const absenceByKey = useMemo(() => {
    const m = new Map<string, StaffAbsenceReason>();
    if (!weekAbsences || weekAbsences.length === 0) return m;
    for (const d of DOWS) {
      const dateIso = isoDate(addDays(anchor, d.key));
      for (const a of weekAbsences) {
        if (dateIso >= a.startDate && dateIso <= a.endDate) {
          m.set(`${a.staffId}#${String(d.key)}`, a.reason);
        }
      }
    }
    return m;
  }, [weekAbsences, anchor]);

  // Unique centres (first-appearance order) → colour + 2-letter code.
  const centres = useMemo(() => {
    const map = new Map<string, string>();
    for (const s of data?.staff ?? []) map.set(s.centerId, s.centerName);
    return [...map.entries()].map(([id, name]) => ({ id, name }));
  }, [data]);

  const centreMeta = useMemo(() => {
    const m = new Map<string, CentreMeta>();
    centres.forEach((c, i) => {
      m.set(c.id, { name: c.name, code: centreCode(c.name), palette: CENTRE_PALETTE[i % CENTRE_PALETTE.length] ?? DEFAULT_PALETTE });
    });
    return m;
  }, [centres]);

  // One row per staff; `centres` = every centre the staff belongs to (from the
  // per-pair entries the API still returns).
  const staffRows = useMemo<StaffRow[]>(() => {
    const map = new Map<string, StaffRow>();
    for (const s of data?.staff ?? []) {
      const row = map.get(s.id);
      if (row) {
        if (!row.centres.some((c) => c.id === s.centerId)) row.centres.push({ id: s.centerId, name: s.centerName });
      } else {
        map.set(s.id, { id: s.id, name: s.name, isActive: s.isActive, centres: [{ id: s.centerId, name: s.centerName }] });
      }
    }
    return [...map.values()];
  }, [data]);

  const visibleStaff = activeCenterId === ALL_CENTERS
    ? staffRows
    : staffRows.filter((s) => s.centres.some((c) => c.id === activeCenterId));

  // Stats — las ausencias restan a horas netas y NO cuentan como turnos libres.
  const totals = useMemo(() => {
    let cells = 0;
    let off = 0;
    let absent = 0;
    let hours = 0;
    let lostHours = 0;
    const byStaff = new Map<string, number>();
    const byCenter = new Map<string, number>();
    for (const s of visibleStaff) {
      let staffHours = 0;
      for (const d of DOWS) {
        cells += 1;
        const cell = cellByDay.get(`${s.id}#${String(d.key)}`);
        const isAbsent = absenceByKey.has(`${s.id}#${String(d.key)}`);
        if (isAbsent) {
          absent += 1;
          if (cell) lostHours += rangeHours(cell.timeRange);
          continue;
        }
        if (!cell) { off += 1; continue; }
        const h = rangeHours(cell.timeRange);
        staffHours += h;
        hours += h;
        byCenter.set(cell.centerId, (byCenter.get(cell.centerId) ?? 0) + h);
      }
      byStaff.set(s.id, staffHours);
    }
    return { cells, off, absent, hours, lostHours, plannedHours: hours + lostHours, byStaff, byCenter };
  }, [visibleStaff, cellByDay, absenceByKey]);

  function exportCsv() {
    const headerRow = ['Profesional', ...DOWS.map((d) => d.label), 'Horas/sem'];
    const rows: (string | number)[][] = [headerRow];
    for (const s of visibleStaff) {
      const dayCells = DOWS.map((d) => {
        const cell = cellByDay.get(`${s.id}#${String(d.key)}`);
        if (!cell) return 'Libre';
        const code = centreMeta.get(cell.centerId)?.code ?? '';
        return `${code} ${cell.timeRange}`.trim();
      });
      rows.push([s.name, ...dayCells, `${String(totals.byStaff.get(s.id) ?? 0)}h`]);
    }
    downloadCsv(`horarios-${weekStart}.csv`, rows);
  }

  const staffForAbsences = useMemo(
    () => staffRows.map((s) => ({
      id: s.id,
      name: s.name,
      centerName: s.centres.length > 1 ? '(varios)' : (s.centres[0]?.name ?? '—'),
    })),
    [staffRows],
  );

  const editingStaff = editing ? staffRows.find((s) => s.id === editing.staffId) ?? null : null;
  const editingExisting = editing ? cellByDay.get(`${editing.staffId}#${String(editing.dayOfWeek)}`) ?? null : null;

  return (
    <div>
      <PageHead
        title="Horarios"
        sub="Asigna a cada profesional su turno y centro por día. Solo superadmin."
        meta={<span><span className="mono">{totals.hours}h</span> · semana</span>}
        actions={
          <>
            <button className="btn" onClick={exportCsv} disabled={visibleStaff.length === 0}>
              <Icons.Download size={14} /> Exportar
            </button>
            <button
              className="btn"
              onClick={() => { setCopying(true); copyPrevWeek.mutate(); }}
              disabled={copyPrevWeek.isPending || copying}
            >
              <Icons.Refresh size={14} /> Copiar sem. anterior
            </button>
          </>
        }
      />

      <div
        className="card"
        style={{
          padding: '10px 14px',
          marginBottom: 16,
          background: 'var(--accent-soft, #f1f5f0)',
          fontSize: 12.5,
          color: 'var(--fg-muted)',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
        }}
      >
        <Icons.AlertTriangle size={14} />
        <span>
          Cada día, el profesional trabaja en un centro. Estos turnos afectan al widget solo en los
          centros que tengan activado <em>Usar horarios para disponibilidad</em> en Configuración.
          Las ausencias (vacaciones / baja) bloquean siempre.
        </span>
      </div>

      <div className="stat-row">
        <div className="stat">
          <div className="stat-label">Profesionales</div>
          <div className="stat-value mono">{visibleStaff.length}</div>
          <div className="stat-delta">{activeCenterId === ALL_CENTERS ? 'todos los centros' : centres.find((c) => c.id === activeCenterId)?.name ?? '—'}</div>
        </div>
        <div className="stat">
          <div className="stat-label">Horas semanales</div>
          <div className="stat-value mono">{totals.hours}h</div>
          <div className="stat-delta">
            {totals.lostHours > 0 ? (
              <span>
                <span style={{ color: 'var(--danger)' }}>−{totals.lostHours}h por ausencias</span>{' '}
                · {totals.plannedHours}h planificadas
              </span>
            ) : (
              <>media {visibleStaff.length > 0 ? (totals.hours / visibleStaff.length).toFixed(1) : '0'}h por persona</>
            )}
          </div>
        </div>
        <div className="stat">
          <div className="stat-label">Turnos libres</div>
          <div className="stat-value mono">{totals.off}</div>
          <div className="stat-delta">
            de {totals.cells} celdas
            {totals.absent > 0 && (
              <> · <span style={{ color: 'var(--danger)' }}>{totals.absent} en ausencia</span></>
            )}
          </div>
        </div>
        <div className="stat">
          <div className="stat-label">Cobertura por centro</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 4 }}>
            {centres.map((c) => {
              const h = totals.byCenter.get(c.id) ?? 0;
              const pct = totals.hours > 0 ? h / totals.hours : 0;
              const meta = centreMeta.get(c.id);
              return (
                <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11.5 }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: meta?.palette.dot ?? DEFAULT_PALETTE.dot, flexShrink: 0 }} />
                  <span style={{ flex: 1, color: 'var(--fg-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</span>
                  <span className="mono cell-bold">{h}h</span>
                  <span className="mono" style={{ color: 'var(--fg-soft)', width: 32, textAlign: 'right' }}>{Math.round(pct * 100)}%</span>
                </div>
              );
            })}
            {centres.length === 0 && <span style={{ fontSize: 11.5, color: 'var(--fg-soft)' }}>—</span>}
          </div>
        </div>
      </div>

      <div className="toolbar">
        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          <button className="icon-btn" onClick={() => { setAnchor((d) => addDays(d, -7)); }} aria-label="Semana anterior">
            <Icons.ChevL size={14} />
          </button>
          <button className="btn" data-size="sm" onClick={() => { setAnchor(getMondayOf(new Date())); }}>
            Hoy
          </button>
          <button className="icon-btn" onClick={() => { setAnchor((d) => addDays(d, 7)); }} aria-label="Semana siguiente">
            <Icons.ChevR size={14} />
          </button>
          <span className="mono" style={{ marginLeft: 12, fontSize: 12, color: 'var(--fg-soft)' }}>{rangeLabel(anchor)}</span>
        </div>
        <div className="toolbar-spacer" />
        <select
          className="select"
          style={{ width: 'auto' }}
          value={activeCenterId}
          onChange={(e) => { setActiveCenterId(e.target.value); }}
        >
          {supportsAll && (
            <option value={ALL_CENTERS}>{isSuper ? 'Todos los centros' : 'Todos mis centros'}</option>
          )}
          {centres.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        {isFetching && <span style={{ fontSize: 12, color: 'var(--fg-soft)' }}>Actualizando…</span>}
        {copyPrevWeek.isError && <span style={{ fontSize: 12, color: 'var(--danger)' }}>No se pudo copiar la semana</span>}
      </div>

      {centres.length > 0 && (
        <div style={{ display: 'flex', gap: 14, alignItems: 'center', margin: '0 0 12px', fontSize: 11.5, color: 'var(--fg-muted)', flexWrap: 'wrap' }}>
          {centres.map((c) => {
            const meta = centreMeta.get(c.id);
            return (
              <span key={c.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                <span style={{ width: 9, height: 9, borderRadius: '50%', background: meta?.palette.dot ?? DEFAULT_PALETTE.dot }} />
                <span className="mono">{meta?.code}</span> · {c.name}
              </span>
            );
          })}
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
            <span style={{ width: 9, height: 9, borderRadius: 2, border: '1px solid var(--border)' }} /> Libre
          </span>
        </div>
      )}

      {isLoading && <div className="card" style={{ padding: 32, textAlign: 'center' }}><span className="spinner" /></div>}
      {error && (
        <div className="card" style={{ padding: 32, color: 'var(--fg-muted)' }}>
          No se pudo cargar los horarios. {error.message}
        </div>
      )}

      {!isLoading && !error && (
        visibleStaff.length === 0 ? (
          <div className="card" style={{ padding: 32, textAlign: 'center', color: 'var(--fg-muted)' }}>
            Sin profesionales en {activeCenterId === ALL_CENTERS ? 'el sistema' : 'este centro'}.
          </div>
        ) : (
          <div className="schedule-grid">
            <table className="table">
              <thead>
                <tr>
                  <th style={{ minWidth: 180 }}>Profesional</th>
                  {DOWS.map((d) => {
                    const dayNum = addDays(anchor, d.key).getDate();
                    return (
                      <th key={d.key} className="cell-right">
                        <div>{d.label}</div>
                        <div style={{ fontSize: 10, color: 'var(--fg-soft)', fontWeight: 400 }}>
                          {String(dayNum)}
                        </div>
                      </th>
                    );
                  })}
                  <th className="cell-right">Horas</th>
                </tr>
              </thead>
              <tbody>
                {visibleStaff.map((s) => (
                  <tr key={s.id}>
                    <td>
                      <div className="cell-bold">{s.name}</div>
                      <div style={{ display: 'flex', gap: 6, marginTop: 3, flexWrap: 'wrap' }}>
                        {s.centres.map((c) => {
                          const meta = centreMeta.get(c.id);
                          return (
                            <span key={c.id} title={c.name} style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 10, color: 'var(--fg-soft)' }}>
                              <span style={{ width: 7, height: 7, borderRadius: '50%', background: meta?.palette.dot ?? DEFAULT_PALETTE.dot }} />
                              <span className="mono">{meta?.code}</span>
                            </span>
                          );
                        })}
                      </div>
                    </td>
                    {DOWS.map((d) => {
                      const cell = cellByDay.get(`${s.id}#${String(d.key)}`);
                      const meta = cell ? centreMeta.get(cell.centerId) : undefined;
                      const tr = cell ? TIME_RANGES.find((r) => r.value === cell.timeRange) : null;
                      const absenceReason = absenceByKey.get(`${s.id}#${String(d.key)}`);
                      const absenceLabel = absenceReason === 'vacation' ? 'Vacaciones'
                        : absenceReason === 'sick' ? 'Baja'
                        : absenceReason === 'other' ? 'Ausente' : null;
                      return (
                        <td key={d.key} className="cell-right">
                          <button
                            type="button"
                            className="schedule-cell"
                            data-state={absenceLabel ? 'absence' : cell ? 'set' : 'empty'}
                            onClick={() => { setEditing({ staffId: s.id, dayOfWeek: d.key }); }}
                            title={absenceLabel
                              ? `${absenceLabel} — el slot no es reservable en el widget`
                              : cell ? meta?.name : undefined}
                            style={absenceLabel ? {
                              background: 'var(--danger-soft)',
                              border: '1px dashed var(--danger)',
                              color: 'var(--danger)',
                            } : cell && meta ? { background: meta.palette.tint } : undefined}
                          >
                            {absenceLabel ? (
                              <>
                                <span style={{ fontSize: 11, fontWeight: 600 }}>{absenceLabel}</span>
                                {cell && (
                                  <span className="mono" style={{ fontSize: 10, color: 'var(--fg-soft)', textDecoration: 'line-through' }}>
                                    {cell.timeRange}
                                  </span>
                                )}
                              </>
                            ) : cell ? (
                              <>
                                <span style={{ display: 'flex', alignItems: 'center', gap: 4, justifyContent: 'flex-end' }}>
                                  <span style={{
                                    background: meta?.palette.dot ?? DEFAULT_PALETTE.dot,
                                    color: '#fff',
                                    borderRadius: 4,
                                    padding: '0 4px',
                                    fontSize: 9,
                                    fontWeight: 700,
                                    lineHeight: '14px',
                                  }}>{meta?.code}</span>
                                  <span className="mono" style={{ fontSize: 11 }}>{cell.timeRange}</span>
                                </span>
                                {tr && <span style={{ fontSize: 10, color: 'var(--fg-soft)' }}>{tr.label}</span>}
                              </>
                            ) : (
                              <span style={{ fontSize: 11, color: 'var(--fg-soft)' }}>—</span>
                            )}
                          </button>
                        </td>
                      );
                    })}
                    <td className="cell-right mono cell-bold">
                      {String(totals.byStaff.get(s.id) ?? 0)}h
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      <AbsencesSection staff={staffForAbsences} />

      {editing && editingStaff && (
        <AssignShiftModal
          staff={editingStaff}
          dayLabel={DOWS.find((d) => d.key === editing.dayOfWeek)?.label ?? ''}
          dayOfWeek={editing.dayOfWeek}
          existing={editingExisting}
          centreMeta={centreMeta}
          centresBh={centresBh}
          isSaving={saveShift.isPending || removeShift.isPending}
          onClose={() => { setEditing(null); }}
          onSave={(centerId, timeRange) => {
            saveShift.mutate({
              staffId: editing.staffId,
              dayOfWeek: editing.dayOfWeek,
              centerId,
              timeRange,
              oldCenterId: editingExisting && editingExisting.centerId !== centerId ? editingExisting.centerId : null,
            });
          }}
          onRemove={() => {
            if (editingExisting) {
              removeShift.mutate({ staffId: editing.staffId, centerId: editingExisting.centerId, dayOfWeek: editing.dayOfWeek });
            }
          }}
        />
      )}
    </div>
  );
}
